import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { getSupplierFacingOrderLabel } from "@/lib/order-display";
import { formatOrderItemPrice } from "@/lib/order-price";
import { OrderService } from "@/services/order-services";
import { SupplierService } from "@/services/supplier-service";
import {
  Document,
  Font,
  Page,
  StyleSheet,
  Text,
  View,
  pdf,
} from "@react-pdf/renderer";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronLeft, Download } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_main/orders/generate-invoice/$orderId")(
  {
    component: InvoiceGenerator,
    loader: async ({ params }: { params: { orderId: string } }) => {
      const order = await OrderService.getOrderById(params.orderId);

      if (!order?.supplier) {
        throw new Error("Order has no supplier");
      }

      const supplier = await SupplierService.getSupplier(order.supplier);

      return { order, supplier };
    },
  },
);

interface InvoiceItem {
  name: string;
  quantity: number;
  uom: string | null;
  currency?: string | null;
  amount?: number | null;
}

const NOTO_SANS_ARABIC_REGULAR =
  "/fonts/Noto_Sans_Arabic/static/NotoSansArabic-Regular.ttf";
const NOTO_SANS_ARABIC_BOLD =
  "/fonts/Noto_Sans_Arabic/static/NotoSansArabic-Bold.ttf";

Font.register({
  family: "Noto Sans Arabic",
  fonts: [
    { src: NOTO_SANS_ARABIC_REGULAR, fontWeight: 400 },
    { src: NOTO_SANS_ARABIC_BOLD, fontWeight: 700 },
  ],
});

function sanitizePdfText(value: string): string {
  const stripped = [
    "\u200B",
    "\u200C",
    "\u200D",
    "\u200E",
    "\u200F",
    "\u2060",
    "\u061C",
    "\uFEFF",
    "\u202A",
    "\u202B",
    "\u202C",
    "\u202D",
    "\u202E",
    "\u2066",
    "\u2067",
    "\u2068",
    "\u2069",
  ];

  let clean = value.normalize("NFKC");
  for (const cp of stripped) {
    clean = clean.split(cp).join("");
  }

  return clean.replace(/\s+/g, " ").trim();
}

function containsArabic(value: string): boolean {
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(value);
}

function toFiniteNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return 0;
}

// Define proper interface for InvoicePDF props
interface InvoicePDFProps {
  invoiceData: {
    invoiceNumber: string;
    orderNumber: string;
    company: {
      name: string;
      address: string;
      // email: string;
      // phone: string;
    };
    billTo: {
      name: string;
      company: string;
      email: string;
      phone: string;
      address: string;
    };
    dates: {
      invoice: string;
      due: string;
    };
    items: InvoiceItem[];
  };
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
  currency: string;
}

// Define styles for PDF outside component to prevent re-creation
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Helvetica",
  },
  section: {
    margin: 10,
    padding: 10,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 12,
    color: "#4b5563",
    marginBottom: 5,
  },
  companyInfo: {
    fontSize: 10,
    color: "#4b5563",
    textAlign: "right",
  },
  separator: {
    height: 1,
    backgroundColor: "#e5e7eb",
    width: "100%",
    marginVertical: 10,
  },
  billInfo: {
    flexDirection: "row",
    alignItems: "flex-start",
    columnGap: 24,
    marginVertical: 20,
  },
  billTo: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    paddingRight: 12,
  },
  billToLabel: {
    fontSize: 10,
    color: "#4b5563",
    marginBottom: 5,
  },
  billToPrimary: {
    fontSize: 10,
    fontWeight: "bold",
    lineHeight: 1.25,
    marginBottom: 1,
  },
  billToAddress: {
    fontSize: 9,
    color: "#4b5563",
    lineHeight: 1.25,
    marginTop: 2,
  },
  dates: {
    width: 260,
    flexShrink: 0,
  },
  dateColumns: {
    flexDirection: "row",
    justifyContent: "space-between",
    columnGap: 24,
    flexWrap: "wrap",
  },
  dateColumn: {
    width: 118,
    alignItems: "flex-start",
    marginBottom: 4,
  },
  dateLabel: {
    fontSize: 10,
    color: "#4b5563",
    marginBottom: 5,
  },
  dateValue: {
    fontSize: 10,
    fontWeight: "bold",
  },
  table: {
    width: "100%",
    marginVertical: 10,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    padding: 8,
    fontSize: 10,
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomColor: "#e5e7eb",
    borderBottomWidth: 1,
    padding: 8,
    fontSize: 9,
  },
  col1: {
    width: "35%",
  },
  col2: {
    width: "20%",
    textAlign: "right",
  },
  col3: {
    width: "20%",
    textAlign: "right",
  },
  col4: {
    width: "25%",
    textAlign: "right",
  },
  unit: {
    fontSize: 8,
    color: "#4b5563",
  },
  summary: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
  },
  summaryDetails: {
    width: "30%",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
    fontSize: 10,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 5,
    borderTopColor: "#e5e7eb",
    borderTopWidth: 1,
    paddingTop: 5,
    fontSize: 12,
    fontWeight: "bold",
  },
  arabicText: {
    fontFamily: "Noto Sans Arabic",
  },
});

// Create PDF Document component outside main component
const InvoicePDF = ({
  invoiceData,
  subtotal,
  tax,
  shipping,
  total,
  currency,
}: InvoicePDFProps) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>INVOICE</Text>
          <Text style={styles.subtitle}>#{invoiceData.invoiceNumber}</Text>
          <Text style={styles.subtitle}>Order #{invoiceData.orderNumber}</Text>
        </View>
        <View>
          <Text
            style={
              containsArabic(invoiceData.company.name)
                ? [styles.companyInfo, styles.arabicText]
                : styles.companyInfo
            }
          >
            {invoiceData.company.name}
          </Text>
          <Text
            style={
              containsArabic(invoiceData.company.address)
                ? [styles.companyInfo, styles.arabicText]
                : styles.companyInfo
            }
          >
            {invoiceData.company.address}
          </Text>
          {/* <Text style={styles.companyInfo}>{invoiceData.company.email}</Text>
          <Text style={styles.companyInfo}>{invoiceData.company.phone}</Text> */}
        </View>
      </View>

      <View style={styles.separator} />

      <View style={styles.billInfo}>
        <View style={styles.billTo}>
          <Text style={styles.billToLabel}>Bill To:</Text>
          <Text
            style={
              containsArabic(invoiceData.billTo.name)
                ? [styles.billToPrimary, styles.arabicText]
                : styles.billToPrimary
            }
          >
            {invoiceData.billTo.name}
          </Text>
          <Text
            style={
              containsArabic(invoiceData.billTo.company)
                ? [styles.billToPrimary, styles.arabicText]
                : styles.billToPrimary
            }
          >
            {invoiceData.billTo.company}
          </Text>
          <Text
            style={
              containsArabic(invoiceData.billTo.address)
                ? [styles.billToAddress, styles.arabicText]
                : styles.billToAddress
            }
          >
            {invoiceData.billTo.address}
          </Text>
        </View>
        <View style={styles.dates}>
          <View style={styles.dateColumns}>
            <View style={styles.dateColumn}>
              <Text style={styles.dateLabel}>Invoice Date:</Text>
              <Text style={styles.dateValue}>{invoiceData.dates.invoice}</Text>
            </View>
            <View style={styles.dateColumn}>
              <Text style={styles.dateLabel}>Due Date:</Text>
              <Text style={styles.dateValue}>{invoiceData.dates.due}</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={[styles.col1]}>Item</Text>
          <Text style={[styles.col2]}>Quantity</Text>
          <Text style={[styles.col3]}>Unit</Text>
          <Text style={[styles.col4]}>Price</Text>
        </View>

        {invoiceData.items.map((item: InvoiceItem, index: number) => (
          <View key={index} style={styles.tableRow}>
            <View style={styles.col1}>
              <Text style={containsArabic(item.name) ? styles.arabicText : {}}>
                {item.name}
              </Text>
            </View>
            <Text style={styles.col2}>{item.quantity}</Text>
            <Text
              style={
                containsArabic(item.uom ?? "")
                  ? [styles.col3, styles.arabicText]
                  : styles.col3
              }
            >
              {item.uom}
            </Text>
            <Text style={styles.col4}>
              {formatOrderItemPrice({
                amount: item.amount,
                currency: item.currency,
              })}
            </Text>
          </View>
        ))}
      </View>

      <View style={styles.summary}>
        <View style={styles.summaryDetails}>
          <View style={styles.summaryRow}>
            <Text style={{ color: "#4b5563" }}>Subtotal</Text>
            <Text>{formatOrderItemPrice({ amount: subtotal, currency })}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={{ color: "#4b5563" }}>Tax (0%)</Text>
            <Text>{formatOrderItemPrice({ amount: tax, currency })}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={{ color: "#4b5563" }}>Shipping:</Text>
            <Text>{formatOrderItemPrice({ amount: shipping, currency })}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text>Total</Text>
            <Text>{formatOrderItemPrice({ amount: total, currency })}</Text>
          </View>
        </View>
      </View>
    </Page>
  </Document>
);

function InvoiceGenerator() {
  const { order, supplier } = Route.useLoaderData();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Extract order details
  const orderItems = Array.isArray(order.order_details)
    ? order.order_details.map((item) => ({
        name: item.name,
        quantity: toFiniteNumber(item.quantity),
        uom: item.uom,
        currency: item.currency,
        amount: toFiniteNumber(item.amount),
      }))
    : [];

  const invoiceData = {
    invoiceNumber: `INV-${getSupplierFacingOrderLabel(order)}-${new Date().getFullYear()}`,
    orderNumber: getSupplierFacingOrderLabel(order),
    company: {
      name: sanitizePdfText(supplier?.name ?? ""),
      address: sanitizePdfText(supplier?.address ?? ""),
    },
    billTo: {
      name: sanitizePdfText(order.restaurant_name),
      company: sanitizePdfText(order.restaurant_name),
      email: "", // Not available in order data
      phone: "", // Not available in order data
      address: sanitizePdfText(order.restaurant_address),
    },
    dates: {
      invoice: new Date().toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
      due: order.expected_delivery_date
        ? new Date(order.expected_delivery_date).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })
        : new Date(
            new Date().setDate(new Date().getDate() + 30),
          ).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          }),
    },
    items: orderItems,
  };

  const invoiceCurrency =
    orderItems.find((item) => {
      return (
        typeof item.currency === "string" && item.currency.trim().length > 0
      );
    })?.currency ?? "USD";

  const subtotal = orderItems.reduce((sum, item) => {
    return sum + toFiniteNumber(item.amount);
  }, 0);
  const tax = 0;
  const shipping = 0;
  const total = subtotal + tax + shipping;

  const handleDownloadPdf = async () => {
    setDownloadError(null);
    setIsDownloading(true);

    try {
      const blob = await pdf(
        <InvoicePDF
          invoiceData={invoiceData}
          subtotal={subtotal}
          tax={tax}
          shipping={shipping}
          total={total}
          currency={invoiceCurrency}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${getSupplierFacingOrderLabel(order)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("[InvoiceGenerator] Failed to generate PDF", error);
      setDownloadError("Failed to generate PDF. Please retry.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="min-h-screen p-8">
      <div className="mb-8 flex items-center justify-between">
        <Button variant="ghost" asChild>
          <a href={`/orders/${order.id}`}>
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back to Order
          </a>
        </Button>
        <Button onClick={handleDownloadPdf} disabled={isDownloading}>
          <Download className="mr-2 h-4 w-4" />
          {isDownloading ? "Preparing..." : "Download PDF"}
        </Button>
      </div>
      {downloadError ? (
        <p className="mb-4 text-destructive text-sm">{downloadError}</p>
      ) : null}

      <div className="mx-auto flex max-w-4xl flex-col gap-8 rounded-lg bg-white shadow-lg">
        <div className="p-8">
          {/* Header */}
          <div className="flex justify-between">
            <div>
              <h1 className="font-medium text-xl">INVOICE</h1>
              <p className="text-gray-600">#{invoiceData.invoiceNumber}</p>
              <p className="text-gray-600 text-xs">
                Order #{invoiceData.orderNumber}
              </p>
            </div>
            <div className="text-right text-sm">
              <h2 className="font-medium">{invoiceData.company.name}</h2>
              <p className="text-gray-600">{invoiceData.company.address}</p>
              {/* <p className="text-gray-600">{invoiceData.company.email}</p>
              <p className="text-gray-600">{invoiceData.company.phone}</p> */}
            </div>
          </div>

          <Separator className="my-4 w-full" />

          {/* Billing Info */}
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="mb-1 text-gray-600 text-sm">Bill To:</h3>
              <p className="font-medium text-sm">{invoiceData.billTo.name}</p>
              <p className="font-medium text-sm">
                {invoiceData.billTo.company}
              </p>
              <p className="text-gray-600 text-sm">
                {invoiceData.billTo.address}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-right text-sm">
              <div className="flex flex-col gap-2">
                <span className="text-gray-600">Invoice Date:</span>
                <span className="ml-4 font-medium">
                  {invoiceData.dates.invoice}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-gray-600">Due Date:</span>
                <span className="ml-4 font-medium">
                  {invoiceData.dates.due}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <table className="w-full px-8">
          <thead>
            <tr className="text-left">
              <th className="bg-gray-100 p-4 font-medium text-xs">Item</th>
              <th className="bg-gray-100 p-4 text-right font-medium text-xs">
                Quantity
              </th>
              <th className="bg-gray-100 p-4 text-right font-medium text-xs">
                Unit
              </th>
              <th className="bg-gray-100 p-4 text-right font-medium text-xs">
                Price
              </th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {invoiceData.items.map((item, index) => (
              <tr
                key={`${item.name}-${index}`}
                className="border-gray-100 border-b"
              >
                <td className="p-4">{item.name}</td>
                <td className="p-4 text-right">{item.quantity}</td>
                <td className="p-4 text-right">{item.uom}</td>
                <td className="p-4 text-right">
                  {formatOrderItemPrice({
                    amount: item.amount,
                    currency: item.currency,
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Summary */}
        <div className="mt-2 flex flex-col items-end p-6">
          <div className="w-64 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal</span>
              <span>
                {formatOrderItemPrice({
                  amount: subtotal,
                  currency: invoiceCurrency,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Tax (0%)</span>
              <span>
                {formatOrderItemPrice({
                  amount: tax,
                  currency: invoiceCurrency,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Shipping:</span>
              <span>
                {formatOrderItemPrice({
                  amount: shipping,
                  currency: invoiceCurrency,
                })}
              </span>
            </div>
            <div className="flex items-center justify-between border-gray-200 border-t pt-2">
              <span className="font-medium">Total</span>
              <span className="font-medium text-lg">
                {formatOrderItemPrice({
                  amount: total,
                  currency: invoiceCurrency,
                })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
